import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@core/models/app-config';
import { BackendAPI } from '@core/models/backend-api';
import { DbConfig } from '@core/models/db-config';
import { BehaviorSubject, Observable, ReplaySubject, catchError, map, of, tap } from 'rxjs';
import { ConfigurationIdentifier } from '../enums/configuration-identifier';

@Injectable({
	providedIn: 'root'
})
export class AppConfigService {

	readonly config$ = new BehaviorSubject<AppConfig | undefined>(undefined);

	// Probably now it needs to be refactored as we can read configuration parameters from the database in separate web-calls, like CargoEnabled.
	readonly dbConfig$ = new ReplaySubject<DbConfig>();

	private dbConfigDefaults: DbConfig = {

	};

	private _databaseConfigParamCache = new Map<ConfigurationIdentifier, string | undefined>();

	protected appConfig: AppConfig;

	constructor (private http: HttpClient) {
		this.appConfig = this.getDefaults();
	}

	init(): Observable<AppConfig> {
		const defaultConfig = this.appConfig;
		return this.http.get('./config/app-config.json?cachebust=' + (new Date().getTime())).pipe(
			map(data => {
				return { ...defaultConfig, ...data };
			}),
			catchError(_ => {
				return of(this.appConfig);
			}),
			tap(data => {
				this.appConfig = data;
				this.config$.next(this.appConfig);

				const getDbConfig = new DbConfig();

				this.dbConfig$.next(Object.assign(getDbConfig, this.dbConfigDefaults));
			})
		);
	}

	get config(): AppConfig {
		return this.appConfig;
	}

	// TODO need a system that allows us to version the defaults. This allows new features to be enabled by default for new installations
	// without breaking previous default behaviour at existing site when they update. Not really sure how to do this yet :L
	private getDefaults(): AppConfig {
		return {
			locales: [
				{
					localeId: "en-AU",
					displayName: "English",
					uiLanguageFile: "en",
					dateFormat: "E d MMM y",
					timestampFormat: "E d MMM h:mma"
				},
				{
					localeId: "fi",
					displayName: "Suomi",
					uiLanguageFile: "fi",
					dateFormat: "E dd.MM.yy",
					timestampFormat: "E dd.MM HH:mm"
				},
				{
					localeId: "sv",
					displayName: "Svenska",
					uiLanguageFile: "sv",
					dateFormat: "E yyyyMMdd",
					timestampFormat: "E yyyyMMdd HH.mm"
				}
			],
			defaultLocaleId: "en-AU",
			editorSymbols: [],
			consoleLogLevel: "",
			enabledPaymentGateways: ["paytrail"], // enable legacy Paytrail by default for backward compatibility with versions prior to 840.1.9.
			currencyCode: "", // TODO replace with currency code on orders rather than system wide config
			restrictedGalleryImagesByElementKey: {},
			guestCanBuildAd: true,
			registrationEnabled: false,
			loginEnabled: false,
			orderCancellationEnabled: false,
			strictPackageGroups: [],
			enableChatToPublishers: undefined,
			autoExportToExternalPlanner: false,
			cargoUploadReadyMaterialUrl: undefined,
			cargoProductionRequestUrl: undefined,
			contactDetailsAddress2FieldMandatory: false,
			contactDetailsStateFieldEnabled: false
		};
	}

	/**
	 * Reads the configuration parameter from the backend's database.
	 * Once it is read, the value is stored in the cache, so all subsequent calls utilize the cache rather than querying the backend repeatedly.
	 * @param identifier
	 * @returns string value if configured or undefined if not
	 */
	private getDatabaseConfigParam(identifier: ConfigurationIdentifier): Observable<string | undefined> {
		if (this._databaseConfigParamCache.has(identifier)) {
			return of(this._databaseConfigParamCache.get(identifier));
		}
		else {
			return this.http.get(`${BackendAPI.systemConfiguration}/${identifier}`, { responseType: "text" }).pipe(
				map(value => {
					this._databaseConfigParamCache.set(identifier, value);
					return value;
				}),
				catchError(error => {
					this._databaseConfigParamCache.set(identifier, undefined); // cache it to prevent further calls to the backend
					throw error;
				})
			);
		}
	}

	/**
	 * Resolves with true if the Cargo integration is enabled in the CAI's database.
	 */
	get isCargoEnabled$(): Observable<boolean> {
		return this.getDatabaseConfigParam(ConfigurationIdentifier.CargoEnabled).pipe(
			map((cargoEnabledValue: string | undefined) => Boolean(cargoEnabledValue && cargoEnabledValue === "1"))
		);
	}
}
